import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { useCart } from '@/contexts/CartContext';
import type { NavigationNode } from '@/hooks/useNavigationApi';

export interface NotificationItem {
  name: string;
  amount: number;
}

export interface UserInfo {
  firstName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email: string;
  phone: string;
  image: string;
  userName: string;
}

interface NavigationContextType {
  quickLinksNodes: NavigationNode[] | null;
  mainMenuNodes: NavigationNode[] | null;
  serviceMenuNodes: NavigationNode[] | null;
  messageBoardPageId: string | null;
  notifications: NotificationItem[] | null;
  notificationTotal: number;
  userInfo: UserInfo | null;
  isLoading: boolean;
  useMock: boolean;
  refreshNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

const SHARED_PARAMS = {
  navigationId: 27,
  expandMode: 'all',
  includeFoldersAndHidden: true,
};

/** Recursively find a node by navigationTag */
function findNodeByTag(nodes: NavigationNode[], tag: string): NavigationNode | null {
  for (const node of nodes) {
    if (node.navigationTag === tag) return node;
    if (node.nodes) {
      const found = findNodeByTag(node.nodes, tag);
      if (found) return found;
    }
  }
  return null;
}

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isFeatureEnabled, flags, isLoading: flagsLoading } = useFeatureFlags();
  const { makeAuthenticatedCall } = useAuthenticatedApi();
  const { fetchActiveCart } = useCart();

  const [quickLinksNodes, setQuickLinksNodes] = useState<NavigationNode[] | null>(null);
  const [mainMenuNodes, setMainMenuNodes] = useState<NavigationNode[] | null>(null);
  const [serviceMenuNodes, setServiceMenuNodes] = useState<NavigationNode[] | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[] | null>(null);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const useMock = isFeatureEnabled('useMockNavigation');
  const useMockNotifications = flags.useMockNotifications;
  const fetchIdRef = useRef(0);

  // Derive messageBoardPageId from MainMenu nodes
  const messageBoardPageId = useMemo(() => {
    if (!mainMenuNodes) return null;
    const node = findNodeByTag(mainMenuNodes, 'MessageBoard');
    return node?.pageId ? String(node.pageId) : null;
  }, [mainMenuNodes]);

  const fetchAllNavigation = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setIsLoading(true);

    try {
      const mainMenuPromise = makeAuthenticatedCall<any>('get-navigation', {
        ...SHARED_PARAMS, rootNavigationTag: 'MainMenu', stopLevel: 3,
      });

      const serviceMenuPromise = makeAuthenticatedCall<any>('get-navigation', {
        ...SHARED_PARAMS, rootNavigationTag: 'ServiceMenu', stopLevel: 3,
      });

      const quickLinksPromise = makeAuthenticatedCall<any>('get-navigation', {
        ...SHARED_PARAMS, rootNavigationTag: 'QuickLinks', stopLevel: 2,
      });

      const userInfoPromise = makeAuthenticatedCall<any>('get-user-info');

      const t2Promise = serviceMenuPromise.then(async (serviceMenu) => {
        if (id !== fetchIdRef.current) return;
        setServiceMenuNodes(serviceMenu.data?.nodes ?? null);

        const serviceNodes: NavigationNode[] = serviceMenu.data?.nodes ?? [];
        const hasNotifications = serviceNodes.some(n => n.navigationTag === 'Notifications');

        const t2Tasks: Promise<void>[] = [
          fetchActiveCart()
        ];

        if (useMockNotifications) {
          const mockItems: NotificationItem[] = [
            { name: 'RGAs Pending Parts Return', amount: 6 },
            { name: 'Claim Requires Feedback', amount: 3 },
            { name: 'New Service Campaigns', amount: 2 },
          ];
          setNotifications(mockItems);
          setNotificationTotal(11);
        } else if (hasNotifications) {
          t2Tasks.push(
            makeAuthenticatedCall<any>('get-notifications', {}).then(notifResult => {
              if (id !== fetchIdRef.current) return;
              const items: NotificationItem[] = (notifResult.data?.notifications ?? []).map((n: any) => ({
                name: n.Name, amount: n.Amount,
              }));
              setNotifications(items);
              setNotificationTotal(items.reduce((sum, i) => sum + i.amount, 0));
            }).catch(err => {
              console.error('Error fetching notifications:', err);
              if (id === fetchIdRef.current) { setNotifications(null); setNotificationTotal(0); }
            })
          );
        } else {
          setNotifications(null); setNotificationTotal(0);
        }

        await Promise.all(t2Tasks);
      });

      const [mainMenu, , quickLinks, userInfoResult] = await Promise.all([
        mainMenuPromise, t2Promise, quickLinksPromise, userInfoPromise,
      ]);

      if (id !== fetchIdRef.current) return;
      setMainMenuNodes(mainMenu.data?.nodes ?? null);
      setQuickLinksNodes(quickLinks.data?.nodes ?? null);
      setUserInfo(userInfoResult.data ?? null);
    } catch (err) {
      console.error('Error fetching navigation:', err);
      if (id === fetchIdRef.current) {
        setQuickLinksNodes(null);
        setMainMenuNodes(null);
        setServiceMenuNodes(null);
        setNotifications(null);
        setNotificationTotal(0);
        setUserInfo(null);
      }
    } finally {
      if (id === fetchIdRef.current) setIsLoading(false);
    }
  }, [makeAuthenticatedCall, fetchActiveCart, useMockNotifications]);

  const clearNavigation = useCallback(() => {
    setQuickLinksNodes(null);
    setMainMenuNodes(null);
    setServiceMenuNodes(null);
    setNotifications(null);
    setNotificationTotal(0);
    setUserInfo(null);
  }, []);

  useEffect(() => {
    if (flagsLoading) return;

    if (!user) {
      clearNavigation();
      setIsLoading(false);
      return;
    }

    if (useMock) {
      clearNavigation();
      setIsLoading(false);
      return;
    }

    fetchAllNavigation();
  }, [user, useMock, flagsLoading, fetchAllNavigation, clearNavigation]);

  const refreshNavigation = useCallback(() => {
    if (user && !useMock && !flagsLoading) {
      fetchAllNavigation();
    }
  }, [user, useMock, flagsLoading, fetchAllNavigation]);

  return (
    <NavigationContext.Provider value={{
      quickLinksNodes,
      mainMenuNodes,
      serviceMenuNodes,
      messageBoardPageId,
      notifications,
      notificationTotal,
      userInfo,
      isLoading: flagsLoading || (!useMock && isLoading),
      useMock,
      refreshNavigation,
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
